import { Injectable } from '@angular/core';
import { AttachmentDTO, CommentDto, CommentsClient, CommentTagDto, DiscussionDto, TaskSubscribersDto, TaskUserSusbscriptionDto } from '../web-api-client';
import { Subject } from 'rxjs';
import { AddCommentDto } from '../models/AddCommentDto';
import { TagReplyParameter } from '../models/TagReplyParameter';
import { EditCommentDto } from '../models/EditCommentDto';

@Injectable({
  providedIn: 'root'
})
export class CommentService {
  setClientReplyTagValue = new Subject<TagReplyParameter>();
  getClientReplyTagValue = this.setClientReplyTagValue.asObservable();
  setCommentReplyTagsList = new Subject<TagReplyParameter[]>();
  getCommentReplyTagsList = this.setCommentReplyTagsList.asObservable();

  deleteCommentEvent = new Subject<string>();
  addCommentAttachmentEvent = new Subject<AttachmentDTO[]>();
  editCommentEvent = new Subject<CommentDto>();
  updateCommentSubscribers = new Subject<TaskSubscribersDto>();

  filteredCommentsUpdated = new Subject<CommentDto[]>(); 

  discussion: DiscussionDto;
  comments: CommentDto[] = [];
  filteredComments: CommentDto[] = [];
  taskSubscribers: TaskSubscribersDto;
  task: any;
  defaultTags: CommentTagDto[];
  tagFilterSelectedValues: string[] = [];

  constructor(private commentsClient: CommentsClient) {
  }

  async getDiscussionByTaskId(taskId: string) {
    this.discussion = await this.commentsClient.get(taskId).toPromise();
    this.comments = this.discussion.comments.reverse().filter(x => x.isDeleted == false);
    return this.discussion;
  }

  async getTaskSubescribersByDiscussionId(discussionId: string) {
    return await this.commentsClient.getDiscussionTaskSubscribers(discussionId).toPromise();
  }

  async getIllustrationTaskSubscriber(discussionId: string, clientSubscribers: TaskUserSusbscriptionDto[], insuropeSubscribers: TaskUserSusbscriptionDto[], networkMemberSubscribers: TaskUserSusbscriptionDto[]) {
    return await this.commentsClient.getIllustrationTaskSubscribers(discussionId, clientSubscribers, insuropeSubscribers, networkMemberSubscribers).toPromise();
  }

  async addComment(commentParams: AddCommentDto) {
    var comment = await this.commentsClient.create(commentParams.taskId,
      commentParams.customSubscriberProvider,
      commentParams.processName,
      commentParams.clientName,
      commentParams.networkMemberName,
      commentParams.country,
      commentParams.periodEnd,
      commentParams.files,
      commentParams.content,
      commentParams.tags,
      commentParams.isDeleted,
      commentParams.isEdited,
      commentParams.isHtmlText,
      commentParams.commentType,
      commentParams.notificationFrequency,
      commentParams.notificationType,
      commentParams.notificationStatus).toPromise()

    if (comment) {
      this.taskSubscribers = await this.addCurrentUserToSubscriber(commentParams.taskId);
    }

    if (comment.attachments?.length > 0) {
      this.addCommentAttachmentEvent.next(comment.attachments);
    }

    this.updateCommentList(comment);

    return comment;
  }

  async downloadFile(taskId: string, discussionId: string, fileId: string) {
    return await this.commentsClient.downloadFile(taskId, discussionId, fileId).toPromise();
  }

  async deleteComment(taskId: string, commentId: string) {

    await this.commentsClient.delete(taskId, commentId).toPromise();
    var index = this.filteredComments.findIndex(x => x.id == commentId);
    if (index != -1) {
      this.comments.splice(index, 1);
    }

    var tagsFilteredBy = this.tagFilterSelectedValues;
    if (tagsFilteredBy.length === 0) {
      this.filteredComments = [...this.comments];
    } else {
      this.filteredComments = this.comments.filter(comment =>
        tagsFilteredBy.every(tag =>
          comment.tags.some(t => t.tagName.toLowerCase().startsWith(tag.toLowerCase()))
        )
      );
    }
    
    this.filteredComments.sort((a, b) => 
      new Date(b.messageSent).getTime() - new Date(a.messageSent).getTime()
    );
    this.filteredCommentsUpdated.next(this.filteredComments);
  }

  async editDiscussion(taskId: string, lastExportedDate: Date) {
    return await this.commentsClient.update(taskId, lastExportedDate).toPromise();
  }

  async addCurrentUserToSubscriber(taskId: string) {
    var updatedSubscibersList = await this.commentsClient.addCurrentUserToSubscribers(taskId).toPromise();
    this.updateCommentSubscribers.next(updatedSubscibersList);
    return updatedSubscibersList;
  }

  async addSubscriber(taskId: string, subscriberId: string, processId: string) {
    return await this.commentsClient.addSubscriber(taskId, subscriberId, processId).toPromise();
  }

  async deleteSubscriber(taskId: string, subscriberId: string) {
    return await this.commentsClient.deleteSubscriber(taskId, subscriberId).toPromise();
  }

  async getAllAttachments(taskId: string) {
    return await this.commentsClient.getAllAttachments(taskId).toPromise();
  }

  async addCommentTag(tagNames: string[], taskId: string, commentId: string) {
    return await this.commentsClient.addCommentTags(tagNames, taskId, commentId).toPromise();
  }

  async getCommentTags(taskId: string, commentId: string) {
    return await this.commentsClient.getCommentTags(taskId, commentId).toPromise();
  }

  async deleteCommentTags(tagId: string, taskId: string, commentId: string) {
    return await this.commentsClient.deleteCommentTags(tagId, taskId, commentId).toPromise();
  }

  async getDefaultTags() {
    if (!this.defaultTags) {
      this.defaultTags = await this.commentsClient.getDefaultTags().toPromise();
      return this.defaultTags;
    }
  }

  async upsertDefaultTag(tagName: string) {
    return await this.commentsClient.upsertDefaultTag(tagName).toPromise();
  }

  async getDiscussionTags(taskId: string, tagName: string) {
    return await this.commentsClient.getDiscussionTags(taskId, tagName).toPromise();
  }

  async filterCommentsByTag(taskId: string, tagName: string) {
    return await this.commentsClient.filterCommentsByTag(taskId, tagName).toPromise();
  }

  async editComment(editComment: EditCommentDto) {
    var editedComment = await this.commentsClient.editComment(editComment.TaskId,
      editComment.CommentId,
      editComment.Content,
      editComment.SendNotification,
      editComment.PeriodEnd,
      editComment.ProcessName,
      editComment.ClientName,
      editComment.NetworkMemberName,
      editComment.CustomSubscriberProvider,
      editComment.Country,
      editComment.Title, null).toPromise();

    let commentIndex = this.comments.findIndex(x => x.id == editedComment.id);
    if (commentIndex != -1) {
      this.comments.splice(commentIndex, 1, editedComment);
    }
    this.updateCommentList(editedComment);
    return editedComment;
  }

  async deleteFileFromComment(taskId, fileId) {
    return this.commentsClient.deleteFileFromComment(taskId, fileId).toPromise()
  }

  setTask(value) {
    this.task = value;
    console.log(this.task)
  }

  fixQuillIndentation(htmlContent: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");

    const listItems = doc.querySelectorAll("li[class*='ql-indent-']");

    listItems.forEach(li => {
      const listItem = li as HTMLElement;
      const indentClass = Array.from(listItem.classList).find(cls => cls.startsWith("ql-indent-"));

      if (indentClass) {
        const indentLevel = parseInt(indentClass.replace("ql-indent-", ""), 10);
        listItem.classList.remove(indentClass);

        // Add all lists to parent
        let parentList = listItem.parentElement;
        if (!parentList || parentList.tagName !== "UL") {
          const newList = document.createElement("ul");
          newList.appendChild(listItem);

          // Add the list in the right place
          if (parentList) {
            parentList.appendChild(newList);
          }
        }

        // Apply visual indentation without deleting elements
        listItem.style.marginLeft = `${indentLevel * 20}px`;
      }
    });

    return doc.body.innerHTML;
  }

  async updateCommentList(comment: CommentDto): Promise<void> {
    var exists = this.comments.find(x => x.id === comment.id);
    if (!exists) {
      this.comments.splice(0, 0, comment);
    }

    const tagsFilteredBy = this.tagFilterSelectedValues;

    if (tagsFilteredBy.length === 0) {
      this.filteredComments = [...this.comments];
    } else {
      this.filteredComments = this.comments.filter(comment =>
        tagsFilteredBy.every(tag =>
          comment.tags.some(t => t.tagName.toLowerCase().startsWith(tag.toLowerCase()))
        )
      );
    }

    this.filteredCommentsUpdated.next(this.filteredComments);
  }
}